import { useEffect, useRef, useState } from 'react';
import { useDataFetchingClient } from 'data-fetching-client';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import unescapedText from 'I18n/utils/unescapedText';
import { useGetInvoicesQuery } from '../../__generated__/chirp/com/hubspot/commerce/buyer/portal/rpc/BuyerBillingDataRpc.hooks';
import { GET_INVOICES_QUERY } from '../../__generated__/chirp/com/hubspot/commerce/buyer/portal/rpc/BuyerBillingDataRpc.dfc.new';
import { useAppDispatch, useAppSelector } from '../redux';
import { clearInvoicePayment, InvoicePaymentStatuses, selectInvoicePaymentStatus } from '../features/InvoicePayment/InvoicePaymentSlice';
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_COUNT = 15;
const POLL_PAGE_LENGTH = 100;
export function usePollInvoicePaid(invoiceId, pollIntervalMs = POLL_INTERVAL_MS) {
  const dispatch = useAppDispatch();
  const status = useAppSelector(selectInvoicePaymentStatus);
  const client = useDataFetchingClient();
  const [isPolling, setIsPolling] = useState(invoiceId !== null);
  const resolvedRef = useRef(false);
  const pollCountRef = useRef(0);
  useEffect(() => {
    if (status !== InvoicePaymentStatuses.COMPLETED_WITHOUT_TARGET) return;

    // Checkout reported success after the payment target was lost, so we can show success but cannot confirm by polling a specific invoice.
    FloatingAlertStore.addAlert({
      message: unescapedText('invoices.invoicePaidSuccessfully'),
      type: 'success'
    });
    dispatch(clearInvoicePayment());
  }, [status, dispatch]);
  useGetInvoicesQuery({
    variables: {
      pageLength: POLL_PAGE_LENGTH,
      filterStatuses: ['past_due']
    },
    skip: !invoiceId || !isPolling,
    pollInterval: pollIntervalMs,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      var _data$result$results;
      if (resolvedRef.current) return;
      pollCountRef.current += 1;
      const isStillPastDue = (_data$result$results = data.result.results) === null || _data$result$results === void 0 ? void 0 : _data$result$results.some(inv => inv.objectId === invoiceId);
      if (pollCountRef.current >= MAX_POLL_COUNT || !isStillPastDue) {
        resolvedRef.current = true;
        setIsPolling(false);
        void client.refetchQueries({
          include: [GET_INVOICES_QUERY]
        });
        if (pollCountRef.current < MAX_POLL_COUNT) {
          // Normal path: polling confirmed that the paid invoice is no longer past due.
          FloatingAlertStore.addAlert({
            message: unescapedText('invoices.invoicePaidSuccessfully'),
            type: 'success'
          });
        }
        dispatch(clearInvoicePayment());
      }
    }
  });
}